import { useState } from 'react'

import { thousands } from '@/lib/format'
import type { FunnelStage } from '@/types'

/**
 * The product funnel (§17): Contacts -> Profiles -> Opportunities -> Approved ->
 * Introductions -> Connections.
 *
 * One series, one colour. Bar length already encodes magnitude, so a ramp would
 * double-encode it and burn the only free channel. The conversion rate between
 * stages is the information a reader actually wants, so it is a direct label.
 */
export function Funnel({ stages }: { stages: FunnelStage[] }) {
  const [showTable, setShowTable] = useState(false)
  const max = Math.max(...stages.map((s) => s.count), 1)

  return (
    <div className="px-4 pb-4 pt-3">
      <div className="mb-2 flex justify-end">
        <button
          type="button"
          className="btn-ghost px-2 py-1 text-xs"
          onClick={() => setShowTable((value) => !value)}
          aria-pressed={showTable}
        >
          {showTable ? 'Show chart' : 'Show table'}
        </button>
      </div>

      {showTable ? (
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th scope="col" className="pb-2 text-left text-xs font-medium uppercase tracking-wide text-ink-faint">
                Stage
              </th>
              <th scope="col" className="pb-2 text-right text-xs font-medium uppercase tracking-wide text-ink-faint">
                Count
              </th>
              <th scope="col" className="pb-2 text-right text-xs font-medium uppercase tracking-wide text-ink-faint">
                Conversion
              </th>
            </tr>
          </thead>
          <tbody>
            {stages.map((stage) => (
              <tr key={stage.key} className="border-t border-line">
                <td className="py-2 text-ink">{stage.label}</td>
                <td className="py-2 text-right tabular text-ink">{thousands(stage.count)}</td>
                <td className="py-2 text-right tabular text-ink-muted">
                  {stage.conversion_from_previous === null
                    ? '—'
                    : `${stage.conversion_from_previous}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <ol className="space-y-1">
          {stages.map((stage, index) => {
            const width = (stage.count / max) * 100
            const wideEnoughForInsideLabel = width > 22

            return (
              <li key={stage.key}>
                {index > 0 && stage.conversion_from_previous !== null && (
                  <p className="py-0.5 pl-[9.5rem] text-xs text-ink-faint">
                    ↳ {stage.conversion_from_previous}% carried through
                  </p>
                )}
                <div className="flex items-center gap-3">
                  <span className="w-36 shrink-0 truncate text-xs text-ink-muted" title={stage.label}>
                    {stage.label}
                  </span>
                  <div className="relative h-6 min-w-0 flex-1">
                    <div
                      className="absolute inset-y-0 left-0 flex items-center"
                      style={{
                        width: `${Math.max(width, stage.count > 0 ? 1.5 : 0)}%`,
                        background: 'var(--series-1)',
                        // Square at the baseline, 4px rounded at the data end.
                        borderRadius: '0 4px 4px 0',
                      }}
                    >
                      {wideEnoughForInsideLabel && (
                        <span className="ml-2.5 text-xs font-medium tabular text-white">
                          {thousands(stage.count)}
                        </span>
                      )}
                    </div>
                    {/* A label that will not fit inside moves outside rather than being clipped. */}
                    {!wideEnoughForInsideLabel && (
                      <span
                        className="absolute inset-y-0 flex items-center text-xs font-medium tabular text-ink"
                        style={{ left: `calc(${Math.max(width, 1.5)}% + 8px)` }}
                      >
                        {thousands(stage.count)}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}
